import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useMutation, useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Link as RouterLink } from 'react-router-dom';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  FormControl,
  FormControlLabel,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  alpha,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { DatePicker } from '@mui/x-date-pickers-pro';

import PageHeader from '~/components/Common/Elements/PageHeader';
import csAgents from '~/components/Common/csAgents.json';

import useAppNavigation from '~/hooks/useAppNavigation';
import useUrlQuery from '~/hooks/useQuery';

import { Dayjs, formatDateYYYYMMDD, getDayJs } from '~/services/TimeService';
import UsersService from '~/services/UsersService';

import SmsVerifyStatsCsvExport from './SmsVerifyStatsCsvExport';
import {
  type CsAgent,
  calculateMaxCount,
  formatDateString,
  getCountColor,
  getFilteredEmailsForChart,
  getSubordinateEmails,
} from './smsVerifyStatsUtils';
import { transformSmsVerificationStats, transformSmsVerificationStatsForTable } from './transformSmsVerificationStats';

const DEFAULT_DAYS = 14;
const BOT_EMAILS = new Set(['lorikeet@luxuryescapes.com']);

type WarningType = 'soft' | 'hard' | 'none' | null;

export default function SmsVerifyStatsPage() {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const brand = useSelector((state: App.State) => state.tenant.brand);
  const location = useLocation();
  const { replace } = useAppNavigation();
  const urlQuery = useUrlQuery();

  // Initialize selectedTeamLeader from URL parameter
  const [selectedTeamLeader, setSelectedTeamLeader] = useState<string>(() => {
    return urlQuery.get('teamLeader') || '';
  });
  const [showWarningLevels, setShowWarningLevels] = useState(false);
  const [warningLevels, setWarningLevels] = useState<Map<string, WarningType>>(new Map());
  const [isLoadingWarningLevels, setIsLoadingWarningLevels] = useState(false);
  const [emailToUserId, setEmailToUserId] = useState<Map<string, string>>(new Map());

  const [startDate, setStartDate] = useState<Dayjs>(getDayJs().subtract(DEFAULT_DAYS, 'day'));
  const [endDate, setEndDate] = useState<Dayjs>(getDayJs());
  const [excludeBots, setExcludeBots] = useState(true);

  // Sync state from URL when URL changes (e.g., browser back/forward)
  // Only sync when location.search changes, not when state changes
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const teamLeaderFromUrl = urlParams.get('teamLeader') || '';
    setSelectedTeamLeader((current) => {
      // Only update if URL value differs from current state
      return teamLeaderFromUrl !== current ? teamLeaderFromUrl : current;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Update URL when team leader selection changes (only if URL doesn't already match)
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const teamLeaderFromUrl = urlParams.get('teamLeader') || '';
    // Only update URL if state doesn't match current URL
    if (selectedTeamLeader !== teamLeaderFromUrl) {
      if (selectedTeamLeader) {
        urlParams.set('teamLeader', selectedTeamLeader);
      } else {
        urlParams.delete('teamLeader');
      }
      const newSearch = urlParams.toString();
      replace(
        {
          hash: location.hash,
          pathname: location.pathname,
          search: newSearch ? `?${newSearch}` : '',
        },
        location.state,
      );
    }
  }, [selectedTeamLeader, location, replace]);

  const startDateStr = useMemo(() => formatDateYYYYMMDD(startDate), [startDate]);
  const endDateStr = useMemo(() => formatDateYYYYMMDD(endDate), [endDate]);

  // Fetch SMS verification stats from API
  const {
    data: rawStatsData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['smsVerificationStats', startDateStr, endDateStr],
    queryFn: async ({ signal }) => {
      return await UsersService.getSmsVerificationStats({
        signal,
        startDate: startDateStr,
        endDate: endDateStr,
      });
    },
  });

  const smsVerificationStatsData = useMemo(() => {
    if (!rawStatsData?.result?.dailyCounts || !excludeBots) return rawStatsData;
    return {
      ...rawStatsData,
      result: {
        ...rawStatsData.result,
        dailyCounts: rawStatsData.result.dailyCounts.filter(
          (agent: { email?: string }) => !agent.email || !BOT_EMAILS.has(agent.email.toLowerCase()),
        ),
      },
    };
  }, [rawStatsData, excludeBots]);

  // Get filtered emails for chart when team leader is selected
  const filteredEmailsForChart = useMemo(() => {
    return getFilteredEmailsForChart(selectedTeamLeader, csAgents as Array<CsAgent>);
  }, [selectedTeamLeader]);

  const chartData = useMemo(() => {
    if (!smsVerificationStatsData) {
      return [];
    }
    return transformSmsVerificationStats(smsVerificationStatsData, filteredEmailsForChart);
  }, [smsVerificationStatsData, filteredEmailsForChart]);

  const allTableData = useMemo(() => {
    if (!smsVerificationStatsData) {
      return [];
    }
    return transformSmsVerificationStatsForTable(smsVerificationStatsData, csAgents, {
      startDate: startDateStr,
      endDate: endDateStr,
    });
  }, [smsVerificationStatsData, startDateStr, endDateStr]);

  // Extract team leaders (top-level agents)
  const teamLeaders = useMemo(() => {
    return (csAgents as Array<CsAgent>).map((agent) => ({
      email: agent.Email,
      name: agent.Name,
    }));
  }, []);

  // Filter table data based on selected team leader
  const tableData = useMemo(() => {
    if (!selectedTeamLeader) {
      return allTableData;
    }
    const subordinateEmails = getSubordinateEmails(selectedTeamLeader, csAgents as Array<CsAgent>);
    return allTableData.filter((row) => subordinateEmails.has(row.email.toLowerCase()));
  }, [allTableData, selectedTeamLeader]);

  // Mutation for updating warning level
  const updateWarningMutation = useMutation({
    mutationFn: async ({ userId, warningType }: { userId: string; warningType: 'soft' | 'hard' | 'none' }) => {
      return await UsersService.setSmsVerificationWarning(userId, warningType);
    },
    onSuccess: (data, variables) => {
      // Update local state with the new warning level
      const email = tableData.find((row) => row.agentId === variables.userId)?.email || '';
      if (email) {
        const newWarningLevel = data.result?.warningType || null;
        setWarningLevels((prev) => {
          const next = new Map(prev);
          next.set(email.toLowerCase(), newWarningLevel);
          return next;
        });
      }
      enqueueSnackbar('Warning level updated successfully', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Error updating warning level: ${error.message}`, { variant: 'error' });
    },
  });

  // Fetch warning levels for all agents, looking up user IDs by email if needed
  const handleFetchWarningLevels = useCallback(async () => {
    setIsLoadingWarningLevels(true);
    try {
      const userIdMap = new Map<string, string>(emailToUserId);
      const warningPromises = tableData.map(async (row) => {
        let userId = row.agentId;

        // If no agentId, try to look up by email
        if (!userId) {
          // Check if we already have this email's userId cached
          const cachedUserId = userIdMap.get(row.email.toLowerCase());
          if (cachedUserId) {
            userId = cachedUserId;
          } else {
            // Look up user by email
            try {
              const usersResponse = await UsersService.getUsers({
                filter: row.email,
                limit: 1,
                brand,
              });
              // getUsers returns an array, find exact email match
              const user = Array.isArray(usersResponse)
                ? usersResponse.find((u) => u.email?.toLowerCase() === row.email.toLowerCase())
                : null;
              if (user?.id_member) {
                userId = user.id_member;
                userIdMap.set(row.email.toLowerCase(), userId);
              }
            } catch (error) {
              // If lookup fails, continue without userId
              console.warn(`Failed to look up user for email ${row.email}:`, error);
            }
          }
        } else {
          // Cache the existing agentId
          userIdMap.set(row.email.toLowerCase(), userId);
        }

        // If we have a userId, fetch warning level
        if (userId) {
          try {
            const response = await UsersService.getSmsVerificationWarning(userId);
            return {
              email: row.email.toLowerCase(),
              warningType: response.result?.warningType || null,
              userId,
            };
          } catch (error) {
            // If error, assume no warning
            return {
              email: row.email.toLowerCase(),
              warningType: null as WarningType,
              userId,
            };
          }
        }

        // No userId found, return null warning
        return {
          email: row.email.toLowerCase(),
          warningType: null as WarningType,
          userId: '',
        };
      });

      const results = await Promise.all(warningPromises);
      const warningMap = new Map<string, WarningType>();
      const newEmailToUserId = new Map<string, string>();

      for (const result of results) {
        warningMap.set(result.email, result.warningType);
        if (result.userId) {
          newEmailToUserId.set(result.email, result.userId);
        }
      }

      setWarningLevels(warningMap);
      setEmailToUserId(newEmailToUserId);
      setShowWarningLevels(true);
    } catch (error) {
      enqueueSnackbar(`Error fetching warning levels: ${error instanceof Error ? error.message : 'Unknown error'}`, {
        variant: 'error',
      });
    } finally {
      setIsLoadingWarningLevels(false);
    }
  }, [tableData, emailToUserId, brand, enqueueSnackbar]);

  const handleWarningLevelChange = useCallback(
    (email: string, agentId: string, newWarningType: 'soft' | 'hard' | 'none') => {
      // Try to get userId from agentId, or from the emailToUserId map
      const userId = agentId || emailToUserId.get(email.toLowerCase());
      if (!userId) {
        enqueueSnackbar('Cannot update warning level: User ID not available', { variant: 'error' });
        return;
      }
      updateWarningMutation.mutate({ userId, warningType: newWarningType });
    },
    [updateWarningMutation, emailToUserId, enqueueSnackbar],
  );

  // Calculate maximum count across all agents and days in the filtered table data
  const maxCount = useMemo(() => {
    return calculateMaxCount(tableData);
  }, [tableData]);

  // Helper function to get color based on count (red for 0, green for max)
  const getCountColorForCell = useCallback(
    (count: number): string => {
      return getCountColor(count, maxCount);
    },
    [maxCount],
  );

  return (
    <Container maxWidth="xl">
      <Helmet>
        <title>SMS Verification usage | Admin</title>
      </Helmet>

      <PageHeader title="SMS Verification usage" />

      <Box mt={1}>
        <Link component={RouterLink} to="/sms-verify-stats/update-agents" variant="body2">
          Update agent list
        </Link>
      </Box>

      <Box mt={4}>
        <Alert severity="info" variant="outlined">
          <Typography variant="body1" component="div">
            <strong>Why SMS Verification?</strong>
            <br />
            <br />
            We're tracking stats on SMS verification per agent to help ensure we're using the most secure verification
            method available.
            <br />
            <br />
            SMS is the <strong>most secure</strong> way to verify, and <strong>required whenever possible</strong>.
            <br />
            After SMS, no other verification step is required. (No need to confirm name, booking number, email, hotel,
            etc, unless things look suspicious or fraudulent)
          </Typography>
        </Alert>
      </Box>

      <Paper sx={{ p: 3, mt: 4 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <DatePicker
            label="Start date"
            value={startDate}
            maxDate={endDate}
            format="DD/MM/YYYY"
            onChange={(v) => v && setStartDate(v)}
            slotProps={{ textField: { size: 'small' } }}
          />
          <DatePicker
            label="End date"
            value={endDate}
            minDate={startDate}
            maxDate={getDayJs()}
            format="DD/MM/YYYY"
            onChange={(v) => v && setEndDate(v)}
            slotProps={{ textField: { size: 'small' } }}
          />
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setStartDate(getDayJs().subtract(14, 'day'));
              setEndDate(getDayJs());
            }}
          >
            Last 2 weeks
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setStartDate(getDayJs().subtract(28, 'day'));
              setEndDate(getDayJs());
            }}
          >
            Last 4 weeks
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setStartDate(getDayJs().subtract(3, 'month'));
              setEndDate(getDayJs());
            }}
          >
            Last 3 months
          </Button>
          <FormControlLabel
            control={<Checkbox checked={excludeBots} onChange={(e) => setExcludeBots(e.target.checked)} />}
            label="Exclude bots"
          />
        </Stack>
      </Paper>

      {error && (
        <Box mt={4}>
          <Alert severity="error">
            Error loading SMS verification stats: {error instanceof Error ? error.message : 'Unknown error'}
          </Alert>
        </Box>
      )}

      {isLoading && (
        <Box mt={4} display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      )}

      {!isLoading && !error && smsVerificationStatsData && (
        <Box mt={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {selectedTeamLeader
                ? `Daily SMS Verification Counts - ${
                    teamLeaders.find((l) => l.email === selectedTeamLeader)?.name || 'Selected Team'
                  }`
                : 'Daily SMS Verification Counts - total across all agents'}
            </Typography>
            <Box mt={3}>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={chartData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tickFormatter={formatDateString} />
                  <YAxis />
                  <Tooltip labelFormatter={formatDateString} />
                  <Line type="monotone" dataKey="totalCount" stroke={theme.palette.primary.main} strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Box>
      )}

      {!isLoading && !error && smsVerificationStatsData && (
        <Box mt={4}>
          <Paper sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="h6">
                Agent Daily Counts ({startDate.format('D MMM YYYY')} – {endDate.format('D MMM YYYY')})
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                <SmsVerifyStatsCsvExport
                  tableData={allTableData}
                  showWarningLevels={showWarningLevels}
                  warningLevels={warningLevels}
                />
                {!showWarningLevels && (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleFetchWarningLevels}
                    disabled={isLoadingWarningLevels}
                    startIcon={isLoadingWarningLevels ? <CircularProgress size={16} /> : null}
                  >
                    View Warning Levels
                  </Button>
                )}
                <FormControl sx={{ minWidth: 250 }}>
                  <InputLabel id="team-leader-select-label">Filter by Team Leader</InputLabel>
                  <Select
                    labelId="team-leader-select-label"
                    id="team-leader-select"
                    value={selectedTeamLeader}
                    label="Filter by Team Leader"
                    onChange={(e) => setSelectedTeamLeader(e.target.value)}
                  >
                    <MenuItem value="">
                      <Typography fontStyle="italic">All Agents</Typography>
                    </MenuItem>
                    {teamLeaders.map((leader) => (
                      <MenuItem key={leader.email} value={leader.email}>
                        {leader.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            </Box>
            <TableContainer>
              <Table size="small" sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Email
                      </Typography>
                    </TableCell>
                    {showWarningLevels && (
                      <TableCell align="center">
                        <Typography variant="subtitle2" fontWeight="bold">
                          Warning Level
                        </Typography>
                      </TableCell>
                    )}
                    {allTableData[0]?.dayCounts.map((dc) => (
                      <TableCell key={dc.date} align="center">
                        <Typography
                          variant="subtitle2"
                          fontWeight="bold"
                          sx={{ whiteSpace: 'nowrap', fontSize: '0.7rem' }}
                        >
                          {formatDateString(dc.date)}
                        </Typography>
                      </TableCell>
                    ))}
                    <TableCell align="center">
                      <Typography variant="subtitle2" fontWeight="bold">
                        Total
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tableData.map((row) => {
                    const currentWarningLevel = warningLevels.get(row.email.toLowerCase()) || null;
                    const displayWarningLevel = currentWarningLevel === null ? 'none' : currentWarningLevel;
                    const userId = row.agentId || emailToUserId.get(row.email.toLowerCase());
                    const hasUserId = !!userId;

                    return (
                      <TableRow key={row.email}>
                        <TableCell>
                          <Typography variant="body2">{row.email}</Typography>
                        </TableCell>
                        {showWarningLevels && (
                          <TableCell align="center">
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                              <Select
                                value={displayWarningLevel}
                                onChange={(e) =>
                                  handleWarningLevelChange(
                                    row.email,
                                    row.agentId,
                                    e.target.value as 'soft' | 'hard' | 'none',
                                  )
                                }
                                disabled={!hasUserId || updateWarningMutation.isPending}
                                sx={(muiTheme) => ({
                                  ...(displayWarningLevel === 'soft' && {
                                    backgroundColor: alpha(muiTheme.palette.warning.main, 0.1),
                                    '& .MuiOutlinedInput-notchedOutline': {
                                      borderColor: muiTheme.palette.warning.main,
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                      borderColor: muiTheme.palette.warning.dark,
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                      borderColor: muiTheme.palette.warning.main,
                                    },
                                  }),
                                  ...(displayWarningLevel === 'hard' && {
                                    backgroundColor: alpha(muiTheme.palette.error.main, 0.1),
                                    '& .MuiOutlinedInput-notchedOutline': {
                                      borderColor: muiTheme.palette.error.main,
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                      borderColor: muiTheme.palette.error.dark,
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                      borderColor: muiTheme.palette.error.main,
                                    },
                                  }),
                                })}
                              >
                                <MenuItem value="none">None</MenuItem>
                                <MenuItem value="soft">Soft</MenuItem>
                                <MenuItem value="hard">Hard</MenuItem>
                              </Select>
                            </FormControl>
                          </TableCell>
                        )}
                        {row.dayCounts.map((dayCount) => (
                          <TableCell
                            key={dayCount.day}
                            align="center"
                            sx={{
                              backgroundColor: getCountColorForCell(dayCount.count),
                            }}
                          >
                            <Typography variant="body2">{dayCount.count}</Typography>
                          </TableCell>
                        ))}
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight="bold">
                            {row.total}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      )}
    </Container>
  );
}
